import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Navigation from './Navigation';
import { Footer } from './Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

// Add a specific error boundary for the content area
class ContentErrorBoundary extends React.Component<{children: React.ReactNode}, {hasError: boolean, error: Error | null}> {
  constructor(props: {children: React.ReactNode}) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null
    };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Content error boundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="text-red-600 dark:text-red-400 text-5xl mb-4">😕</div>
            <h2 className="text-xl font-semibold mb-2">Oops! Something went wrong</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              There was a problem displaying this content.
            </p>
            <button
              onClick={this.handleRetry}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full mt-2 px-4 py-2 border border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 rounded transition"
            >
              Go To Home Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const Layout: React.FC = () => {
  const location = useLocation();
  const { theme } = useTheme();

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="min-h-screen flex flex-col bg-background dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Navigation />
        <AnimatePresence mode="wait">
          <motion.main 
            key={location.pathname}
            className="flex-grow pt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="w-full max-w-full px-2 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8 mx-auto">
              <ContentErrorBoundary>
                <Outlet />
              </ContentErrorBoundary>
            </div>
          </motion.main>
        </AnimatePresence>
        <Footer />
      </div>
    </div>
  );
};

export default Layout; 